import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { InformationSource } from "@/types/information-source/information-source.types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { infoSourceService } from "@/services/information-source/infoSourceService";

interface InformationSourceFormProps {
  isOpen: boolean;
  onClose: () => void;
  sourceToEdit?: InformationSource | null;
  onSuccess: () => void;
}

export const InformationSourceForm = ({ isOpen, onClose, sourceToEdit, onSuccess }: InformationSourceFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  // Form state
  const [name, setName] = useState("");
  const [year, setYear] = useState<number | undefined>(undefined);
  const [link, setLink] = useState("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [yearError, setYearError] = useState("");

  // Initialize form when editing
  useEffect(() => {
    if (sourceToEdit) {
      setName(sourceToEdit.name || "");
      setYear(sourceToEdit.year);
      setLink(sourceToEdit.link || "");
    } else {
      // Reset form for new creation
      setName("");
      setYear(undefined);
      setLink("");
    }
    // Reset validation errors when opening/closing the form
    setNameError("");
    setYearError("");
  }, [sourceToEdit, isOpen]);

  const validateYear = (value?: number): string => {
    if (value === undefined || value === null) {
      return t("validation.required");
    } else if (value < 1990 || value > currentYear) {
      return t("validation.yearRange") + ` (1990-${currentYear})`;
    }
    return "";
  };

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate year
    const yearValidationError = validateYear(year);
    setYearError(yearValidationError);
    if (yearValidationError) {
      isValid = false;
    }

    return isValid;
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setYear(undefined);
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setYear(numValue);
    }
  };

  const handleYearBlur = () => {
    setYearError(validateYear(year));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const sourceData: Partial<InformationSource> = {
        name: name.trim(),
        year: year || 0,
        // Always include link field to allow removal - send null/empty string if field is empty
        link: link && link.trim() !== "" ? link.trim() : null,
      };

      if (sourceToEdit) {
        // Update existing source
        await infoSourceService.edit(sourceToEdit.id, sourceData, user?.uid);
        toast({
          title: t("success"),
          description: t("infoSources.updated"),
        });
      } else {
        // Create new source
        await infoSourceService.create(
          sourceData as Omit<InformationSource, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("infoSources.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving information source:", error);

      // Handle specific API errors
      if (error.message === "YEAR_REQUIRED") {
        toast({
          title: t("error"),
          description: t("infoSources.errors.yearRequired"),
          variant: "destructive",
        });
      } else if (error.message === "INFO_SOURCE_DUPLICATE") {
        toast({
          title: t("error"),
          description: t("infoSources.errors.duplicateSource"),
          variant: "destructive",
        });
      } else {
        // Generic error handling
        toast({
          title: t("error"),
          description: sourceToEdit ? t("infoSources.errors.updateError") : t("infoSources.errors.createError"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{sourceToEdit ? t("infoSources.editTitle") : t("infoSources.addTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {t("infoSources.name")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  if (!name.trim()) {
                    setNameError(t("validation.required"));
                  } else {
                    setNameError("");
                  }
                }}
                placeholder={t("infoSources.namePlaceholder")}
                className={`placeholder:text-gray-400 placeholder:text-sm ${nameError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {nameError && <p className="text-sm font-medium text-red-500">{nameError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="year">
                {t("infoSources.year")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="year"
                type="number"
                min={1990}
                max={currentYear}
                placeholder={`${t("infoSources.yearPlaceholder")} (1990-${currentYear})`}
                value={year || ""}
                onChange={handleYearChange}
                onBlur={handleYearBlur}
                className={`placeholder:text-gray-400 placeholder:text-sm ${yearError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {yearError && <p className="text-sm font-medium text-red-500">{yearError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="link">{t("infoSources.link")}</Label>
              <Input
                id="link"
                type="text"
                placeholder={t("infoSources.linkPlaceholder")}
                value={link}
                onChange={(e) => setLink(e.target.value)}
                className="placeholder:text-gray-400 placeholder:text-sm"
                disabled={isLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-teal-600 hover:bg-teal-700 text-white">
              {isLoading ? t("saving") : sourceToEdit ? t("update") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
